import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';

import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { dataGridCol } from '@shared/components/grid/common';
import { formatPhoneNumber, openPdf, withQueryParams } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';

import { Wrapper } from '@styles/CommonStyles';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { CancelButton } from '@shared/inputs/Buttons';
import { useServerSide } from '@shared/hooks/useServerSide';
import { requestCreateShipment, requestRemoveShipment } from './queries';
import { IShipment, IShipmentData } from './types';

export const FundedShipment = () => {
  const pageSize = 15;
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
  const [items, setItems] = useState<IShipment[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const { requestPayload, onSortModelChange, onFilterModelChange, onPageChange } = useServerSide();

  const { data: fundedShipments, mutate, isLoading: loading } = useSWR<IShipmentData>(
  withQueryParams(`/${SERVICES.DEAL}/deals/funded/shipments/filtered`),
  (url: string) =>
    axios.post(url, { ...requestPayload, pageSize: pageSize }),
    { revalidateIfStale: true}
  );

  useEffect(() => {
    mutate();
  }, [requestPayload]);

  useEffect(() => {
    setItems(fundedShipments?.data?.items || []);
    setTotalCount(fundedShipments?.data?.totalCount || 0);
  }, [fundedShipments]);

  useEffect(() => {
    if (fundedShipments) {
      setItems(fundedShipments?.data?.items);
      setTotalCount(fundedShipments?.data?.totalCount);
    }
  }, [fundedShipments]);

  const columns: GridColDef[] = [
    dataGridCol('dealNumericId', { 
      headerName: 'Deal',
      flex:1.3,
      minWidth:100
    }),
    dataGridCol('contactName', { 
      flex:1,
      minWidth: 100 
    }),
    dataGridCol('lcName' ,{
      flex:1,
      minWidth: 100 
    }),
    dataGridCol('lenderName', {
      flex:1.3,
      minWidth: 120 
    }),
    dataGridCol('payeeName', { 
      flex:1.2, 
      minWidth: 120,
    }),
    dataGridCol('payeeStreet', { 
      flex:1.5,
      minWidth: 100,
     }),
    dataGridCol('payeeCity', {
      flex:1.2,
      minWidth:100
    }),
    dataGridCol('payeeState', {
      flex:1.2,
      minWidth:100
    }),
    dataGridCol('payeeZip', {
      flex:1.2,
      minWidth:100
    }),
    dataGridCol('payeePhoneNumber', {
      flex:1.3,
      minWidth: 120,
      valueFormatter: ({ value }) => formatPhoneNumber(value),
    }),
  ].map((i) => ({ editable: true,...i }));

  const onCreateShipment = useCallback(async() => {
    const data = items.filter((shipment: IShipment) => selectedIds.includes(shipment.id));
    setIsLoading(true);
    const resp = await requestCreateShipment(data);
    setIsLoading(false);
    resp?.data.file && openPdf(resp.data.file);
    mutate();
  }, [items, selectedIds]);

  const onRemoveClick = useCallback(async() => {
    setIsLoading(true);
    await requestRemoveShipment(selectedIds);
    setIsLoading(false);
    mutate();
  }, [selectedIds]);

  const handleCheckboxSelection = (ids: GridSelectionModel) => {
    setSelectedIds(ids);
  };

  return (
    <Wrapper>
      <LoaderBackdrop open={isLoading} />
      <PhoenixGridHeader title=''>
        <CancelButton variant="contained" label="Remove From List" sx={{ position: 'relative', top: '40px', right: '135px' }} onClick={onRemoveClick} />
      </PhoenixGridHeader>
      <PhoenixGrid
        autoHeight
        withHeader
        checkboxSelection
        disableSelectionOnClick
        showClearAllFilter
        paginationMode="server"
        sortingMode='server'
        filterMode='server'
        buttonTitle="Create Shipment"
        disabled={selectedIds.length === 0}
        name="Funded Shipment"
        pageSize={15}
        getRowId={(row) => row.id}
        columns={columns}
        loading={loading}
        rows={items && !isLoading ? items : []}
        rowCount={totalCount}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onButtonClick={onCreateShipment}
        onPageChange={onPageChange}
        onSelectionModelChange={handleCheckboxSelection}
      />
    </Wrapper>
  );
};

